import { diag, DiagConsoleLogger, DiagLogLevel, trace } from '@opentelemetry/api'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import type { Instrumentation } from '@opentelemetry/instrumentation'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request'
import { resourceFromAttributes } from '@opentelemetry/resources'
import { SEMRESATTRS_SERVICE_NAME, SEMRESATTRS_DEPLOYMENT_ENVIRONMENT, SEMRESATTRS_SERVICE_VERSION } from '@opentelemetry/semantic-conventions'
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'

export interface TelemetryConfig {
    serviceName: string
    environment?: string
    collectorUrl?: string
    serviceVersion?: string
}

let provider: WebTracerProvider | null = null
let registeredInstrumentations: Instrumentation[] = []

let loggerInitialised = false

const initLogger = (): void => {
    if (loggerInitialised) {
        return
    }

    const level =
        import.meta.env.DEV && !import.meta.env.PROD
            ? DiagLogLevel.DEBUG
            : DiagLogLevel.ERROR

    diag.setLogger(new DiagConsoleLogger(), level)
    loggerInitialised = true
}

export const initTelemetry = ({
    serviceName,
    environment,
    collectorUrl,
    serviceVersion,
}: TelemetryConfig): (() => Promise<void>) | undefined => {
    if (typeof window === 'undefined') {
        return undefined
    }

    if (provider) {
        return undefined
    }

    initLogger()

    const resource = resourceFromAttributes({
        [SEMRESATTRS_SERVICE_NAME]: serviceName,
        [SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: environment ?? 'local',
        [SEMRESATTRS_SERVICE_VERSION]:
            serviceVersion ?? import.meta.env.VITE_GIT_SHA ?? 'dev',
    })

    provider = new WebTracerProvider({ resource })

    if (collectorUrl) {
        const exporter = new OTLPTraceExporter({ url: collectorUrl })
        const processor = new BatchSpanProcessor(exporter)
        // Note: addSpanProcessor API changed in v2.x, using workaround
        // @ts-ignore - addSpanProcessor method signature changed
        provider.addSpanProcessor?.(processor)
    }

    provider.register()

    // Mapbox tile/style requests are cross-origin and reject traceparent in CORS preflight.
    const thirdPartyIgnoreUrls = [/api\.mapbox\.com/, /events\.mapbox\.com/]

    const propagateTraceHeaderCorsUrls = collectorUrl ? [collectorUrl] : undefined

    registeredInstrumentations = [
        new FetchInstrumentation({
            // Same-origin /api calls still get trace headers by default; only opt in to extra CORS targets.
            propagateTraceHeaderCorsUrls,
            ignoreUrls: thirdPartyIgnoreUrls,
            clearTimingResources: true,
        }),
        new XMLHttpRequestInstrumentation({
            ignoreUrls: thirdPartyIgnoreUrls,
        }),
    ]

    registerInstrumentations({
        instrumentations: registeredInstrumentations,
    })

    return async () => {
        registeredInstrumentations.forEach((instrumentation) =>
            instrumentation.disable(),
        )
        registeredInstrumentations = []

        if (provider) {
            await provider.shutdown()
            provider = null
        }
    }
}

/**
 * Gets the tracer for creating spans
 */
export const getTracer = () => {
    return trace.getTracer('CenterReach.Web', '1.0.0')
}